import React, { useEffect, useState } from 'react';
import { themeService, templateService, Theme, type ThemeTemplate, type ThemeCategory } from '../services/themeService';
import { projectStore } from '../lib/projectStore';
import '../styles/InsightModal.css';
import '../styles/AddThemeModal.css';

interface AddThemeModalProps {
  onClose: () => void;
  onSaved: (theme: Theme) => void;
  existingThemes: Theme[];
}

type Step = 'method' | 'templates' | 'parent';

const AddThemeModal: React.FC<AddThemeModalProps> = ({ onClose, onSaved, existingThemes }) => {
  const [step, setStep] = useState<Step>('method');
  const [selectedTemplate, setSelectedTemplate] = useState<ThemeTemplate | null>(null);
  const [templateCategory, setTemplateCategory] = useState<string>('all');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Templates loaded from the DB
  const [templates, setTemplates] = useState<ThemeTemplate[]>([]);
  const [categories, setCategories] = useState<ThemeCategory[]>([]);
  const [loading, setLoading] = useState(false);

  // Load templates + categories the first time the user opens the template picker
  useEffect(() => {
    if (step === 'templates' && templates.length === 0 && !loading) {
      setLoading(true);
      Promise.all([templateService.getTemplates(), templateService.getCategories()])
        .then(([tpls, cats]) => { setTemplates(tpls); setCategories(cats); })
        .catch((err) => console.error('Failed to load templates:', err))
        .finally(() => setLoading(false));
    }
  }, [step, templates.length, loading]);

  // Reset state when a template is selected (or when going back to method)
  useEffect(() => {
    if (step === 'method') {
      setSelectedTemplate(null);
      setTemplateCategory('all');
    }
  }, [step]);

  const filteredTemplates = templateCategory === 'all'
    ? templates
    : templates.filter(t => t.category === templateCategory);

  const handleSelectTemplate = (tpl: ThemeTemplate) => {
    setSelectedTemplate(tpl);
    setName(tpl.title);
    setDescription(tpl.description);
    setStep('parent');
  };

  const handleManualNext = () => {
    if (!name.trim()) return;
    setStep('parent');
  };

  const handleBack = () => {
    if (step === 'parent') {
      if (selectedTemplate) {
        setStep('templates');
      } else {
        setStep('method');
      }
    } else if (step === 'templates') {
      setStep('method');
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const created = await themeService.createTheme({
        name: name.trim(),
        description: description.trim() || null,
        project_id: projectStore.getProjectId(),
        parent_id: parentId,
        source: selectedTemplate ? 'template' : 'manual',
        template_id: selectedTemplate?.id ?? null,
      });
      onSaved(created);
    } catch (err) {
      console.error('Error creating theme:', err);
      alert(`Failed to create theme: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Step: method picker ───────────────────────────────
  if (step === 'method') {
    return (
      <div className="cum-modal-backdrop" onClick={onClose}>
        <div className="inline-edit-form premium-task-modal at-theme-modal" onClick={e => e.stopPropagation()}>
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>Add Theme</h3>
              <p className="header-subtitle-new">Choose how you want to create a theme</p>
            </div>
            <button type="button" className="task-modal-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="at-method-list">
            <button type="button" className="at-method-option" onClick={() => setStep('parent')}>
              <div className="at-method-icon" style={{ background: '#ede9fe', color: '#6d28d9' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
                </svg>
              </div>
              <div className="at-method-info">
                <div className="at-method-title">Manual</div>
                <div className="at-method-desc">Create a theme from scratch with a name and description.</div>
              </div>
              <svg className="at-method-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>

            <button type="button" className="at-method-option" onClick={() => setStep('templates')}>
              <div className="at-method-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                </svg>
              </div>
              <div className="at-method-info">
                <div className="at-method-title">Templates</div>
                <div className="at-method-desc">Start from a curated theme template with suggested keywords.</div>
              </div>
              <svg className="at-method-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

          <div className="task-modal-footer">
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step: template picker ──────────────────────────────
  if (step === 'templates') {
    return (
      <div className="cum-modal-backdrop" onClick={onClose}>
        <div className="inline-edit-form premium-task-modal at-theme-modal at-templates-modal" onClick={e => e.stopPropagation()}>
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>Theme Templates</h3>
              <p className="header-subtitle-new">Pick a template to start from</p>
            </div>
            <button type="button" className="task-modal-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="at-templates-layout">
            {/* Sidebar */}
            <div className="at-templates-sidebar">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className={`at-tpl-cat ${templateCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setTemplateCategory(cat.id)}
                >
                  {cat.id === 'all' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                    </svg>
                  )}
                  {cat.id === 'product' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    </svg>
                  )}
                  {cat.id === 'leadership' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M3 9h18"/><path d="M9 21V9"/>
                    </svg>
                  )}
                  {cat.id === 'cx' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      <path d="M12 8a2 2 0 0 0-2 2c0 1.5 2 2 2 4" />
                    </svg>
                  )}
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="at-templates-grid">
              {filteredTemplates.map(tpl => (
                <button
                  key={tpl.id}
                  type="button"
                  className="at-tpl-card"
                  onClick={() => handleSelectTemplate(tpl)}
                >
                  <div className="at-tpl-card-header">
                    <div className={`at-tpl-card-icon at-tpl-icon-${tpl.icon}`}>
                      {tpl.icon === 'box' && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        </svg>
                      )}
                      {tpl.icon === 'heart' && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      )}
                      {tpl.icon === 'whiteboard' && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <path d="M3 9h18"/><path d="M9 21V9"/>
                        </svg>
                      )}
                    </div>
                    <div className="at-tpl-card-title">{tpl.title}</div>
                  </div>
                  <div className="at-tpl-card-desc">{tpl.description}</div>
                  <div className="at-tpl-card-keywords">
                    {tpl.keywords.slice(0, 4).map(k => (
                      <span key={k} className="at-tpl-keyword">{k}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="task-modal-footer">
            <button className="cancel-btn" onClick={handleBack}>Back</button>
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step: parent picker + final form ───────────────────
  return (
    <div className="cum-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="inline-edit-form premium-task-modal at-theme-modal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <div className="header-titles-new">
            <h3>{selectedTemplate ? `Create from "${selectedTemplate.title}"` : 'Create Theme'}</h3>
            <p className="header-subtitle-new">Name your theme and optionally place it under a parent theme</p>
          </div>
          <button type="button" className="task-modal-close" onClick={onClose} disabled={saving}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="at-form">
          <div className="form-group-new">
            <label className="section-label-new">Name <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              type="text"
              className="task-title-input-new"
              placeholder="e.g. Change Management"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group-new">
            <label className="section-label-new">Description</label>
            <textarea
              className="at-form-textarea"
              placeholder="Optional — describe what this theme covers"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="form-group-new">
            <label className="section-label-new">Parent theme <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
            <div className="at-parent-list">
              <button
                type="button"
                className={`at-parent-option ${parentId === null ? 'selected' : ''}`}
                onClick={() => setParentId(null)}
              >
                <span className="at-parent-radio">
                  {parentId === null && <span className="at-parent-radio-dot" />}
                </span>
                <div className="at-parent-info">
                  <span className="at-parent-name">No parent — make it a top-level theme</span>
                </div>
              </button>
              {existingThemes.length === 0 && (
                <div className="at-parent-empty">No parent themes exist yet.</div>
              )}
              {existingThemes.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`at-parent-option ${parentId === t.id ? 'selected' : ''}`}
                  onClick={() => setParentId(t.id)}
                >
                  <span className="at-parent-radio">
                    {parentId === t.id && <span className="at-parent-radio-dot" />}
                  </span>
                  <div className="at-parent-info">
                    <span className="at-parent-name">{t.name}</span>
                    {t.description && <span className="at-parent-desc">{t.description.slice(0, 60)}{t.description.length > 60 ? '…' : ''}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="task-modal-footer">
          <button className="cancel-btn" onClick={handleBack} disabled={saving}>Back</button>
          <button
            className="submit-btn"
            onClick={handleCreate}
            disabled={saving || !name.trim()}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddThemeModal;
